import React, { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { fetchSanity } from '../lib/sanity';
// Helper to convert a name like "Edith Yeung" to "edith-yeung"
const generateAuthorSlug = (name: string) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};
const MOCK_POSTS = [{
  _id: 'mock-1',
  title: 'The AI Platform for the Software That Runs the World',
  slug: 'blog',
  author: 'Edith Yeung',
  publishedAt: '2026-05-20T12:00:00Z',
  imageUrl: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=2865&auto=format&fit=crop',
  excerpt: 'How our latest investment is transforming the way enterprise software is built, deployed, and scaled across global infrastructure.'
}, {
  _id: 'mock-2',
  title: 'Why We Invested in the Future of Compute',
  slug: 'future-of-compute',
  author: 'Chris McCann',
  publishedAt: '2026-04-12T12:00:00Z',
  imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2940&auto=format&fit=crop',
  excerpt: 'A deep dive into the silicon and systems powering the next generation of artificial intelligence.'
}, {
  _id: 'mock-3',
  title: 'The Rise of Agentic Workflows in Enterprise',
  slug: 'agentic-workflows',
  author: 'Race Capital Team',
  publishedAt: '2026-03-28T12:00:00Z',
  imageUrl: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2944&auto=format&fit=crop',
  excerpt: 'Moving beyond simple chatbots to autonomous agents that can execute complex, multi-step business processes.'
}];
export const BlogPage = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMock, setIsMock] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);
  const getPosts = async () => {
    setLoading(true);
    setDebugError(null);
    try {
      // Updated query to use publishedAt field, falling back to _createdAt
      const query = `*[_type == "post"] | order(coalesce(publishedAt, _createdAt) desc) {
        _id,
        title,
        "slug": coalesce(slug.current, _id),
        author,
        "publishedAt": coalesce(publishedAt, _createdAt),
        "imageUrl": mainImage.asset->url
      }`;
      const data = await fetchSanity(query);
      if (data && data.length > 0) {
        setPosts(data);
        setIsMock(false);
      } else {
        setDebugError("Query successful, but 0 published posts were found. Did you hit 'Publish' in Sanity?");
        setPosts(MOCK_POSTS);
        setIsMock(true);
      }
    } catch (err: any) {
      console.error('Failed to fetch posts:', err);
      // Check if it's likely a CORS error (TypeError: Failed to fetch)
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        setDebugError("CORS Error: Your browser blocked the request. Please go to manage.sanity.io -> API -> CORS Origins and add 'https://magicpatterns.com' (with credentials unchecked).");
      } else {
        setDebugError(err.message || 'Unknown network error');
      }
      setPosts(MOCK_POSTS);
      setIsMock(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPosts();
  }, []);
  return <div className="w-full bg-white mt-20 min-h-screen" data-id="element-99">
      {/* Hero Section - Mirroring Insights Page */}
      <section className="w-full pt-20 pb-10 px-6 md:px-12 bg-gradient-to-b from-white to-gray-50/50" data-id="element-100">
        <div className="max-w-7xl mx-auto" data-id="element-101">
          <div className="relative mb-6 overflow-visible" data-id="element-102">
            <style jsx data-id="element-103">{`
              @keyframes fillFromLeft {
                from {
                  transform: scaleX(0);
                  transform-origin: left;
                }
                to {
                  transform: scaleX(1);
                  transform-origin: left;
                }
              }
              .racing-stripe {
                transform: scaleX(0);
                transform-origin: left;
                animation: fillFromLeft 0.8s ease-out forwards;
              }
              .racing-stripe:nth-child(1) {
                animation-delay: 0.1s;
              }
              .racing-stripe:nth-child(2) {
                animation-delay: 0.2s;
              }
              .racing-stripe:nth-child(3) {
                animation-delay: 0.3s;
              }
              .racing-stripe:nth-child(4) {
                animation-delay: 0.4s;
              }
            `}</style>
            <div className="absolute right-[12px] top-0 hidden md:flex flex-col space-y-2 pointer-events-none" data-id="element-104">
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/30 to-red-600/10" style={{
              width: '240px',
              marginLeft: '0px'
            }} data-id="element-105"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/35 to-red-600/15" style={{
              width: '200px',
              marginLeft: '40px'
            }} data-id="element-106"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/40 to-red-600/20" style={{
              width: '160px',
              marginLeft: '80px'
            }} data-id="element-107"></div>
              <div className="racing-stripe h-1.5 bg-gradient-to-r from-red-600/45 to-red-600/25" style={{
              width: '120px',
              marginLeft: '120px'
            }} data-id="element-108"></div>
            </div>
            <h1 className="relative z-10 text-[clamp(2.5rem,5vw,4rem)] font-semibold text-[#070707] tracking-tight leading-[1.15]" data-id="element-109">
              Blog
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed font-light mb-6" data-id="element-110">
            Latest news, announcements, and deep dives from the Race Capital
            team and our portfolio companies.
          </p>
          <div className="flex flex-wrap items-center gap-4" data-id="element-111">
            {isMock && <div className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium" data-id="element-112">
                <span className="w-2 h-2 rounded-full bg-amber-500 mr-2 animate-pulse" data-id="element-113"></span>
                Showing sample data.
              </div>}
          </div>

          {debugError && <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-mono" data-id="element-114">
              <strong data-id="element-115">Debug Info:</strong> {debugError}
            </div>}
        </div>
      </section>

      {/* Articles Grid */}
      <section className="w-full pt-10 pb-20 px-6 md:px-12 bg-white" data-id="element-116">
        <div className="max-w-7xl mx-auto" data-id="element-117">
          {loading ? <div className="flex justify-center items-center py-20" data-id="element-118">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" data-id="element-119"></div>
            </div> : posts.length === 0 ? <div className="bg-gray-50 text-gray-500 p-12 rounded-2xl border border-gray-100 text-center" data-id="element-120">
              <h3 className="text-xl font-semibold text-[#070707] mb-2" data-id="element-121">
                No posts found
              </h3>
              <p data-id="element-122">
                Create your first post in your Sanity Studio to see it appear
                here.
              </p>
              <p className="text-sm mt-4" data-id="element-123">
                Make sure you have a schema named "post" and have added this
                website's URL to your Sanity CORS origins.
              </p>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-124">
              {posts.map(post => <Link key={post._id} to={`/blog/${post.slug}`} className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-red-600/10 transition-all hover:-translate-y-1 flex flex-col h-full" data-id="element-125">
                  {post.imageUrl ? <div className="w-full h-48 overflow-hidden bg-gray-100" data-id="element-126">
                      <img src={post.imageUrl} alt={post.title || 'Blog post'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-127" />
                    </div> : <div className="w-full h-48 bg-gray-50 flex flex-col items-center justify-center border-b border-gray-100 px-4 text-center" data-id="element-128">
                      <span className="text-gray-400 font-medium mb-1" data-id="element-129">
                        No Image
                      </span>
                      <span className="text-gray-400 text-xs" data-id="element-130">
                        Recommended: 800 × 400px (2:1 ratio)
                      </span>
                    </div>}
                  <div className="p-8 flex flex-col flex-grow" data-id="element-131">
                    <h3 className="text-xl font-bold text-[#070707] mb-3 group-hover:text-red-600 transition-colors" data-id="element-132">
                      {post.title || 'Untitled Post'}
                    </h3>
                    {post.excerpt && <p className="text-gray-600 text-sm mb-6 leading-relaxed flex-grow" data-id="element-133">
                        {post.excerpt}
                      </p>}
                    <div className="flex items-center text-sm text-gray-500 mt-auto pt-4 border-t border-gray-100" data-id="element-134">
                      {post.author && <span className="font-medium relative z-10" data-id="element-135">
                          {post.author.split(',').map((authorName: string, index: number, array: string[]) => {
                    const cleanName = authorName.trim();
                    return <Fragment key={index} data-id="element-136">
                                    <span className="hover:text-red-600 transition-colors cursor-pointer" onClick={e => {
                        e.preventDefault();
                        window.location.href = `/team/${generateAuthorSlug(cleanName)}`;
                      }} data-id="element-137">
                                      {cleanName}
                                    </span>
                                    {index < array.length - 1 && ', '}
                                  </Fragment>;
                  })}
                        </span>}
                      {post.author && post.publishedAt && <span className="mx-2" data-id="element-138">•</span>}
                      {post.publishedAt && <span data-id="element-139">
                          {new Date(post.publishedAt).toLocaleDateString()}
                        </span>}
                    </div>
                  </div>
                </Link>)}
            </div>}
        </div>
      </section>
    </div>;
};